/* eslint-disable react-hooks/exhaustive-deps */
// eslint-disable-next-line @typescript-eslint/no-use-before-define
import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Helmet } from 'react-helmet'
import { Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom'
import { ThemeProvider, CSSReset } from '@chakra-ui/core'
import customTheme from './theme'
import { RootState } from './constants/interfaces'
import {
  IntegrationGuard,
  RequireAuth,
  RequireLiveRid,
  RequireMfa,
  RequireJobsAccess,
  RequireCompetitionsAccess,
  RequireAdminAccess,
  RequirePromptsAccess,
  RequireFeatureAccess,
  RequireAgentAccess,
} from './routes'
import { FEATURE_FLAGS } from 'constants/featureFlags'
import Auth from './services/authService'
import { jwtDecode } from 'jwt-decode'
import { Navigate } from 'react-router-dom'

import {
  handleConfig,
  handleInitialTranslations,
  handleUserWritePermitted,
  handleSetTaskPermittedGroup,
  setConfigActiveGroupId,
} from 'redux/actions/config'
import { handleTasks } from './redux/actions/tasks'

import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import {
  LoadableNewsFeed,
  LoadableTask,
  LoadableTasksOverview,
  LoadableNewTaskDashboard,
  LoadableTasksOverviewDetail,
  LoadableTasksOverviewSubtasks,
  LoadableTasksList,
  LoadableLogin,
  LoadableTaskViews,
  LoadableTaskViewsTasks,
  LoadableTaskViewsRsc,
  LoadableTaskViewsSvc,
  LoadableRanking,
  LoadableRankingsList,
  LoadableIntegrations,
  LoadableImprovingSales,
  LoadableCampaignDetail,
  LoadableReports,
  LoadableServiceCallsDashboard,
  LoadableAnalytics,
  LoadableSetup,
  LoadableSalesOpportunities,
  LoadableProductPerformance,
  LoadableOrgnizationTree,
  LoadableTasksAnalyticsDashboard,
  LoadableCreateTaskV2,
  LoadableExternalIframePage,
  LoadableAgentPage,
  LoadableMySchedules,
  LoadableLibrary,
} from 'routes/loadableComponents'
import MainLoader from 'components/CommonComponents/MainLoader'
import keys from 'constants/keys'
import { Inactions } from 'components/Inactions'
import { ChangePassword } from 'pages/ChangePassword'
import { LoginConfirm } from 'pages/LoginConfirm'
import { isLoggedInSelector } from 'redux/selectors/auth'
import { isRtlSelector } from 'redux/selectors/general'
import {
  LayoutWithMainSidebar,
  LayoutWithTaskOverviewSidebar,
  LayoutWithTasksSidebar,
  LayoutWithoutSidebar,
  LayoutWithAgentSidebar,
} from 'components/CommonComponents/MainLayout'
import { GlobalStyles } from 'components/GlobalStyles'

import { ForgotPassword, ForgotPasswordSubmit } from 'pages'
import {
  getRanking,
  setRankingFilter,
  setRankingFilterList,
  setRankingReports,
  setRankingSort,
} from 'redux/actions/ranking'
import { handleGetPops } from 'redux/actions/pops'
import { logEvent, setUserProperties, setUserId as setAmplitudeUserId } from 'firebase/analytics'
import * as posthogService from 'services/posthog'
import { setUserId } from 'redux/actions/auth'

import UploadAvatarPopUp from 'components/UploadAvatarPopUp'
import PopModal from 'components/PopOverview'
import { analytics } from './config/firebase'
import ExternalSite from 'pages/ExternalSite'
import { ChatPage } from 'pages/ChatPage'

import { DeepDiveReport } from 'pages/DeepDiveReport'
import { getTasks } from 'redux/actions/tasks_v2'
import { ConfigErrorHandler } from 'components/CommonComponents/ConfigErrorHandler'
import { LoginSSO } from 'pages/LoginSSO'
import { getAWSTokenData, logoutUser, setIsLoggedIn, setToken } from 'redux/actions/auth'
import { FlashModal } from 'components/CommonComponents/FlashModal'
import { setResponseStatus } from 'redux/actions/general'
import TaskInsights from 'pages/TaskInsights'
import { getServiceCalls } from 'redux/actions/serviceCall'
import { handleMarkSeen } from 'redux/actions/notifications'
import { subscribeToPush } from 'services/webPushService'
import { handleNotificationClick } from 'services/notifications/handleNotificationClick'
import { NavigateFn } from 'services/notifications/types'
import { usePushNotificationMessages } from 'hooks/usePushNotificationMessages'
import { NotificationToastStack } from 'components/Notifications/NotificationToastStack'
import { NotificationToastErrorBoundary } from 'components/Notifications/NotificationToastErrorBoundary'
import { NotificationPostModalHost } from 'components/Notifications/NotificationPostModalHost'
import { NotificationChatModalHost } from 'components/Notifications/NotificationChatModalHost'
import { NotificationServiceCallModalHost } from 'components/Notifications/NotificationServiceCallModalHost'
import { TaskOverviewModalHost } from 'components/TasksV2/TasksOverview/TaskOverviewModalHost'
import { AdminConsole } from './components/AdminConsole'
import UsersBoard from './components/AdminConsole/Users'
import TagsBoard from 'components/AdminConsole/Tags'
import JobsBoard from 'components/AdminConsole/Jobs'
import PromptsBoard from 'components/AdminConsole/Promts'
import LoginOTP from 'pages/LoginOTP'
import AdminLoginAs from 'pages/AdminLoginAs'
import NotificationBootstrap, { PENDING_PUSH_NOTIFICATION_KEY } from 'pages/NotificationBootstrap'
import { CompetitionsBoard } from 'components/AdminConsole/Competitions'
import { getRetailCampaigns } from 'redux/actions/campaigns'
import dayjs from 'dayjs'
import { getUTCOffset } from 'utils'
import { SetupProvider } from 'contexts/SetupContext'
import { SetupGuard } from 'components/Setup/SetupGuard'
import { SetupDrawer } from 'components/Setup/SetupDrawer'
import { SetupModalWrapper } from 'components/Setup/SetupModalWrapper'

const HubRedirect = () => {
  const { viewId } = useParams<{ viewId: string }>()
  return <Navigate to={`${keys.ROUTE_NAMES.TASKS}/${viewId}`} replace />
}

// usePushNotificationMessages renders Chakra components (via NotificationToastStack), which need
// a ThemeProvider ancestor — App itself renders <ThemeProvider> as part of its own JSX below, so
// it isn't one yet at the point App's body runs. Render this as a child *inside* that ThemeProvider
// instead of calling the hook directly in App.
const PushNotificationMessagesListener: React.FC<{ navigate: NavigateFn }> = ({ navigate }) => {
  const { toasts, dismissToast, markSeen } = usePushNotificationMessages(navigate)
  const isRtl = useSelector(isRtlSelector)
  const dispatch = useDispatch()
  const resetKey = toasts.map((toast) => toast.id).join(':')

  return (
    <NotificationToastErrorBoundary resetKey={resetKey}>
      <NotificationToastStack
        toasts={toasts}
        onDismiss={dismissToast}
        onMarkSeen={markSeen}
        navigate={navigate}
        isRtl={isRtl}
        dispatch={dispatch}
      />
    </NotificationToastErrorBoundary>
  )
}

function App({ code }: { code: string }) {
  const location = useLocation()
  const navigate = useNavigate()
  const firstStage = process.env.REACT_APP_INACTION_FIRST_STAGE || keys.inactionsTime.firstStage
  const secondStage = process.env.REACT_APP_INACTION_SECOND_STAGE || keys.inactionsTime.secondStage
  const { uid } = useSelector((state: RootState) => state.auth)
  const dispatch = useDispatch()
  const isLoggedIn = useSelector(isLoggedInSelector)
  const [isSSOLoading, setIsSSOLoading] = useState(false)
  const { profile_img_url } = useSelector((state: RootState) => state.config.config)
  const { permission } = useSelector((state: RootState) => state.config.config)
  const { isResponseSuccess } = useSelector((state: RootState) => state.general)

  const {
    isConfigReady,
    retailConfig,
    activeGroupID,
    isConfigLoading,
    configErrorStatus,
    config: { rid, vapid_public_key },
  } = useSelector((state: RootState) => state.config)
  const { locale } = useSelector((state: RootState) => state.general)

  // A view-as tab must never trust redux-persist's rehydrated isLoggedIn/uid/token — persist:auth
  // is shared, unlocked localStorage across EVERY open tab on this origin, each with its own
  // independent in-memory store; whichever tab's write lands there last wins, regardless of which
  // tab that is. So if another Storee tab is open and persists around the same time a view-as tab
  // boots, it can silently overwrite what the view-as tab just set up. sessionStorage is exclusive
  // to this tab and immune to that race, so it's the only thing a view-as tab's identity is allowed
  // to come from — re-asserted fresh on every boot here, never assumed from persisted state.
  useEffect(() => {
    const bootstrapViewAsSession = async () => {
      if (!Auth.isViewAsSession()) {
        // No live view-as credential in this tab. If the marker says the last identity set up in
        // this browser WAS a view-as one, that's leftover from a tab that's since closed (or this
        // is a different tab entirely) — persisted isLoggedIn/uid has nothing backing it here.
        if (Auth.hadViewAsSessionMarker()) {
          Auth.clearViewAsSessionMarker()
          if (isLoggedIn) {
            dispatch(logoutUser())
          }
        }
        return
      }

      const { idToken } = await Auth.getTokens()
      if (!idToken) return
      const decoded = jwtDecode(idToken) as { [key: string]: any }
      dispatch(setToken(idToken))
      dispatch(setUserId(decoded['cognito:username']))
      dispatch(setIsLoggedIn(true))
      // Also reset any group carried over in persisted config state — same cross-tab-clobber risk
      // as isLoggedIn/uid above, and a stale group 500s GET /config?group=<stale-id> for whichever
      // user this view-as tab actually belongs to.
      dispatch(setConfigActiveGroupId(null))
    }
    bootstrapViewAsSession()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const resetStatus = useCallback(() => {
    dispatch(setResponseStatus(null))
  }, [dispatch])

  const fetchConfig = useCallback(() => {
    try {
      dispatch(handleConfig())
      dispatch(handleSetTaskPermittedGroup())
      dispatch(handleUserWritePermitted())
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err)
    }
  }, [isLoggedIn])

  useEffect(() => {
    if (isLoggedIn) {
      fetchConfig()
    } else {
      dispatch(handleInitialTranslations())
    }
  }, [isLoggedIn])

  useEffect(() => {
    if (isConfigReady) {
      dispatch(handleGetPops())
      dispatch(getTasks())
      dispatch(getServiceCalls(activeGroupID!))
      dispatch(handleTasks())
    }
  }, [isConfigReady])

  useEffect(() => {
    // Never register this browser as a push-notification device while an admin is viewing-as
    // a user — it isn't their own device, and would overwrite/steal their actual registration.
    if (!isConfigReady || !isLoggedIn) return

    if (!vapid_public_key) {
      console.warn('[WebPush] skipped: config.vapid_public_key is missing')
      return
    }

    if (Auth.isViewAsSession()) {
      if (process.env.NODE_ENV === 'development') console.info('[WebPush] skipped: admin view-as session')
      return
    }

    subscribeToPush(vapid_public_key).catch((err) => console.warn('[WebPush] subscription failed:', err))
  }, [isConfigReady, isLoggedIn, vapid_public_key])

  // Cold-start pickup: the SW stashed a clicked notification in sessionStorage (see
  // NotificationBootstrap) before the app had any session — resolve it now that auth/config
  // are actually ready.
  useEffect(() => {
    if (!isConfigReady || !isLoggedIn) return
    const raw = sessionStorage.getItem(PENDING_PUSH_NOTIFICATION_KEY)
    if (!raw) return
    sessionStorage.removeItem(PENDING_PUSH_NOTIFICATION_KEY)
    try {
      handleNotificationClick(JSON.parse(raw), navigate, {
        dispatch,
        markSeen: (notification) => {
          dispatch(handleMarkSeen({ seenKeys: [notification.chrono_key] }))
        },
      }).catch((err) => console.warn('[Notifications] cold-start click failed:', err))
    } catch (err) {
      console.warn('[Notifications] failed to parse pending push notification:', err)
    }
  }, [dispatch, isConfigReady, isLoggedIn, navigate])

  useEffect(() => {
    if (permission?.users !== 3) {
      return
    }
    const startDate = new Date()
    const endDate = new Date(new Date().setMonth(new Date().getMonth() + 1))
    const from_ts = dayjs(startDate).format(keys.DATE_FORMAT_API)
    const to_ts = dayjs(endDate).format(keys.DATE_FORMAT_API)
    dispatch(
      getRetailCampaigns({
        from_ts: Number(from_ts),
        to_ts: Number(to_ts),
        utc_offset: getUTCOffset(),
        campaign_type: 1,
      })
    )
  }, [dispatch, permission?.users])

  useEffect(() => {
    if (isConfigReady && activeGroupID !== null) {
      const rankingTags = retailConfig?.ranking_tags

      dispatch(setRankingReports({}))
      dispatch(setRankingFilterList([]))
      dispatch(
        getRanking({
          firstRequest: true,
          kpis_data: true,
          kpis_list: true,
          rankingTag: rankingTags?.[0],
          ...(retailConfig?.data_filters &&
          Array.isArray(retailConfig?.data_filters) &&
          retailConfig?.data_filters.length > 0
            ? { dataFilter: retailConfig?.data_filters[0].values[0] }
            : {}),
        })
      )
    }
  }, [isConfigReady, retailConfig?.ranking_types])

  useEffect(() => {
    if (!location.pathname.includes('ranking')) {
      dispatch(setRankingSort(1))
      dispatch(setRankingFilter(keys.RANKING_DEFAULT_FILTER_VIEW))
    }
  }, [dispatch, location.pathname])

  useEffect(() => {
    logEvent(analytics, 'storee_screen_view', { currentScreen: location.pathname })
    posthogService.captureEvent('$pageview', { $current_url: location.pathname })
  }, [location])

  useEffect(() => {
    if (activeGroupID !== null && rid !== '' && uid) {
      setUserProperties(analytics, { storee_user_id: uid })
      setUserProperties(analytics, { retail_id: rid })
      setAmplitudeUserId(analytics, uid)
      posthogService.identifyUser(uid, { retail_id: rid })
    }
  }, [activeGroupID, rid])
  const hasValidConfig = isConfigReady && !isConfigLoading
  const [isUploadAvatarOpen, setIsUploadAvatarOpen] = useState(false)

  const closeUploadAvatar = useCallback(() => {
    setIsUploadAvatarOpen(false)
  }, [])

  useEffect(() => {
    if (!isLoggedIn || !isConfigReady) return
    if (!profile_img_url) {
      setIsUploadAvatarOpen(true)
    }
  }, [isLoggedIn, isConfigReady, profile_img_url])

  const ssoSignIn = useCallback(async () => {
    if (code) {
      setIsSSOLoading(true)
      const tokensObject = await getAWSTokenData(code)

      if (tokensObject) {
        try {
          await Auth.handleAuthenticatedUserManually(tokensObject)
          const payload: any = jwtDecode(tokensObject.id_token)
          const userId = payload?.['cognito:username']
          dispatch(setToken(tokensObject.id_token))
          dispatch(setUserId(userId))
        } catch (err) {
          console.log(err)
        } finally {
          setIsSSOLoading(false)
        }
      }
    }
  }, [code, dispatch])

  useEffect(() => {
    ssoSignIn()
  }, [ssoSignIn])

  return (
    <ThemeProvider theme={customTheme}>
      <CSSReset />
      <PushNotificationMessagesListener navigate={navigate} />
      <NotificationPostModalHost />
      <NotificationChatModalHost />
      <NotificationServiceCallModalHost />
      <TaskOverviewModalHost />
      <GlobalStyles />
      <Helmet>
        <html lang={locale} dir={locale === 'he' ? 'rtl' : 'ltr'} />
      </Helmet>

      <MainLoader
        isShow={isSSOLoading || (!!isLoggedIn && !configErrorStatus && (isConfigLoading || !isConfigReady))}
      />

      {!hasValidConfig && !!isLoggedIn && configErrorStatus && <ConfigErrorHandler status={configErrorStatus} />}
      {isResponseSuccess && (
        <FlashModal
          status={isResponseSuccess}
          resetStatus={resetStatus}
          text={typeof isResponseSuccess === 'string' ? isResponseSuccess : 'post_published_successfully'}
        />
      )}
      <>
        <SetupProvider>
          <SetupGuard>
            <UploadAvatarPopUp
              isOpen={isUploadAvatarOpen}
              onClose={closeUploadAvatar}
              onUploadFromGallery={closeUploadAvatar}
              onTakeSelfie={closeUploadAvatar}
              onRemindLater={closeUploadAvatar}
            />
            {!isUploadAvatarOpen && <PopModal />}
            <SetupModalWrapper />
            <Routes>
              <Route path={keys.ROUTE_NAMES.LOGIN_PAGE} element={<LoadableLogin />} />
              <Route
                path={keys.ROUTE_NAMES.LOGIN_CONFIRM}
                element={
                  <RequireMfa>
                    <LoginConfirm />
                  </RequireMfa>
                }
              />
              <Route
                path={keys.ROUTE_NAMES.CHANGE_PASSWORD}
                element={
                  <RequireMfa>
                    <ChangePassword />
                  </RequireMfa>
                }
              />
              <Route path={keys.ROUTE_NAMES.LOGIN_OTP} element={<LoginOTP />} />
              <Route path={keys.ROUTE_NAMES.LOGIN_SSO} element={<LoginSSO />} />
              {/* No RequireAuth — this route establishes a session from an admin-tool-issued code, it has none yet. */}
              <Route path={keys.ROUTE_NAMES.ADMIN_LOGIN_AS} element={<AdminLoginAs />} />
              {/* No RequireAuth — the SW may open this before there's any session; it just stashes the pending notification and redirects to '/'. */}
              <Route path={keys.ROUTE_NAMES.NOTIFICATION_OPEN} element={<NotificationBootstrap />} />
              <Route path={keys.ROUTE_NAMES.FORGOT_PASSWORD} element={<ForgotPassword />} />
              <Route path={keys.ROUTE_NAMES.FORGOT_PASSWORD_SUBMIT} element={<ForgotPasswordSubmit />} />

              <Route path="/" element={<LayoutWithMainSidebar />}>
                <Route
                  path={keys.ROUTE_NAMES.SETUP}
                  element={
                    <RequireAuth>
                      <LoadableSetup />
                    </RequireAuth>
                  }
                />
                <Route
                  path={keys.ROUTE_NAMES.NEWS_FEED}
                  element={
                    <RequireAuth>
                      <LoadableNewsFeed />
                    </RequireAuth>
                  }
                />
                <Route
                  path={keys.ROUTE_NAMES.EXTERNAL_APP}
                  element={
                    <RequireAuth>
                      <LoadableExternalIframePage />
                    </RequireAuth>
                  }
                />
                <Route
                  path={keys.ROUTE_NAMES.LIBRARY}
                  element={
                    <RequireAuth>
                      <LoadableLibrary />
                    </RequireAuth>
                  }
                />
                <Route
                  path={keys.ROUTE_NAMES.LIBRARY_FOLDER()}
                  element={
                    <RequireAuth>
                      <LoadableLibrary />
                    </RequireAuth>
                  }
                />
                <Route
                  path={keys.ROUTE_NAMES.NEWS_FEED_SEARCH}
                  element={
                    <RequireAuth>
                      <LoadableNewsFeed />
                    </RequireAuth>
                  }
                />
                <Route
                  path={keys.ROUTE_NAMES.RANKING()}
                  element={
                    <RequireAuth>
                      <LoadableRanking />
                    </RequireAuth>
                  }
                />
                <Route
                  path={keys.ROUTE_NAMES.RANKINGS_LIST}
                  element={
                    <RequireAuth>
                      <LoadableRankingsList />
                    </RequireAuth>
                  }
                />

                <Route
                  path={keys.ROUTE_NAMES.INTEGRATIONS}
                  element={
                    <RequireAuth>
                      <IntegrationGuard>
                        <LoadableIntegrations />
                      </IntegrationGuard>
                    </RequireAuth>
                  }
                />
                <Route
                  path={keys.ROUTE_NAMES.DASHBOARD}
                  element={
                    <RequireAuth>
                      <LoadableTasksOverview />
                    </RequireAuth>
                  }
                />
                <Route
                  path={keys.ROUTE_NAMES.NEW_TASK_DASHBOARD}
                  element={
                    <RequireAuth>
                      <LoadableNewTaskDashboard />
                    </RequireAuth>
                  }
                />
                <Route
                  path={keys.ROUTE_NAMES.SERVICE_CALLS_DASHBOARD}
                  element={
                    <RequireAuth>
                      <LoadableServiceCallsDashboard />
                    </RequireAuth>
                  }
                />
                <Route
                  path={keys.ROUTE_NAMES.MY_SCHEDULES}
                  element={
                    <RequireAuth>
                      <LoadableMySchedules />
                    </RequireAuth>
                  }
                />
                <Route
                  path={keys.ROUTE_NAMES.ANALYTICS}
                  element={
                    <RequireAuth>
                      <RequireAdminAccess>
                        <LoadableAnalytics />
                      </RequireAdminAccess>
                    </RequireAuth>
                  }
                />
                <Route
                  path={keys.ROUTE_NAMES.PRODUCT_PERFORMANCE}
                  element={
                    <RequireAuth>
                      <RequireFeatureAccess flagKey={FEATURE_FLAGS.PRODUCT_PERFORMANCE}>
                        <LoadableProductPerformance />
                      </RequireFeatureAccess>
                    </RequireAuth>
                  }
                />
                <Route
                  path={keys.ROUTE_NAMES.PRODUCT_PERFORMANCE_STORE()}
                  element={
                    <RequireAuth>
                      <RequireFeatureAccess flagKey={FEATURE_FLAGS.PRODUCT_PERFORMANCE}>
                        <LoadableProductPerformance />
                      </RequireFeatureAccess>
                    </RequireAuth>
                  }
                />
                <Route
                  path={keys.ROUTE_NAMES.TASKS_CONTEXTS_LIST}
                  element={
                    <RequireAuth>
                      <TaskInsights />
                    </RequireAuth>
                  }
                />
                <Route
                  path={keys.ROUTE_NAMES.TASKS_ANALYTICS_DASHBOARD}
                  element={
                    <RequireAuth>
                      <LoadableTasksAnalyticsDashboard />
                    </RequireAuth>
                  }
                />

                <Route
                  path={`${keys.ROUTE_NAMES.REPORTS}/:targetGroupId/`}
                  element={
                    <RequireAuth>
                      <LoadableReports />
                    </RequireAuth>
                  }
                />
                <Route
                  path={`${keys.ROUTE_NAMES.REPORTS}/:targetGroupId/:range_type/:component_Id/:sub_component_id/:component_filter`}
                  element={
                    <RequireAuth>
                      <DeepDiveReport />
                    </RequireAuth>
                  }
                />

                <Route path={keys.ROUTE_NAMES.TASKS}>
                  <Route
                    index
                    element={
                      <RequireAuth>
                        <LoadableTaskViewsTasks />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path=":viewId"
                    element={
                      <RequireAuth>
                        <LoadableTasksList />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path=":viewId/:tid"
                    element={
                      <RequireAuth>
                        <LoadableTasksList />
                      </RequireAuth>
                    }
                  />
                </Route>

                <Route path={keys.ROUTE_NAMES.RSC}>
                  <Route
                    index
                    element={
                      <RequireAuth>
                        <LoadableTaskViewsRsc />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path=":viewId"
                    element={
                      <RequireAuth>
                        <LoadableTasksList />
                      </RequireAuth>
                    }
                  />
                </Route>

                <Route path={keys.ROUTE_NAMES.SVC}>
                  <Route
                    index
                    element={
                      <RequireAuth>
                        <LoadableTaskViewsSvc />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path=":viewId"
                    element={
                      <RequireAuth>
                        <LoadableTasksList />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path=":viewId/:scId"
                    element={
                      <RequireAuth>
                        <LoadableTasksList />
                      </RequireAuth>
                    }
                  />
                </Route>

                <Route path={keys.ROUTE_NAMES.HUB} element={<Navigate to={keys.ROUTE_NAMES.TASKS} replace />} />
                <Route
                  path={`${keys.ROUTE_NAMES.HUB}/:viewId`}
                  element={
                    <RequireAuth>
                      <HubRedirect />
                    </RequireAuth>
                  }
                />

                <Route
                  path={keys.ROUTE_NAMES.EXTERNAL_SITE}
                  element={
                    <RequireAuth>
                      <ExternalSite />
                    </RequireAuth>
                  }
                />
                <Route
                  path={keys.ROUTE_NAMES.ORGANIZATION_TREE}
                  element={
                    <RequireAuth>
                      <LoadableOrgnizationTree />
                    </RequireAuth>
                  }
                />
                <Route
                  path={keys.ROUTE_NAMES.SALES_OPPORTUNITIES}
                  element={
                    <RequireAuth>
                      <LoadableSalesOpportunities />
                    </RequireAuth>
                  }
                />
                <Route
                  path={keys.ROUTE_NAMES.CREATE_TASK_V2}
                  element={
                    <RequireAuth>
                      <RequireLiveRid>
                        <LoadableCreateTaskV2 />
                      </RequireLiveRid>
                    </RequireAuth>
                  }
                />
              </Route>
              <Route path={keys.ROUTE_NAMES.DASHBOARD_DETAIL()} element={<LayoutWithTaskOverviewSidebar />}>
                <Route
                  path={keys.ROUTE_NAMES.DASHBOARD_DETAIL()}
                  element={
                    <RequireAuth>
                      <LoadableTasksOverviewDetail />
                    </RequireAuth>
                  }
                />
                <Route
                  path={keys.ROUTE_NAMES.DASHBOARD_SUBTASKS()}
                  element={
                    <RequireAuth>
                      <LoadableTasksOverviewSubtasks />
                    </RequireAuth>
                  }
                />
              </Route>

              <Route path={keys.ROUTE_NAMES.SUBTASKS()} element={<LayoutWithTasksSidebar />}>
                <Route
                  index
                  element={
                    <RequireAuth>
                      <LoadableTask />
                    </RequireAuth>
                  }
                />
              </Route>
              <Route path={keys.ROUTE_NAMES.IMPROVING_SALES_DETAIL()} element={<LayoutWithTasksSidebar />}>
                <Route
                  index
                  element={
                    <RequireAuth>
                      <LoadableTask />
                    </RequireAuth>
                  }
                />
              </Route>
              {/*<Route path={keys.ROUTE_NAMES.CONNECTOR_CALLBACK} element={<ConnectorCallback />} />*/}
              <Route path="*" element={<Navigate to={keys.ROUTE_NAMES.NEWS_FEED} />} />
              <Route path={keys.ROUTE_NAMES.CHAT_PAGE} element={<LayoutWithoutSidebar />}>
                <Route
                  path={keys.ROUTE_NAMES.CHAT_PAGE}
                  element={
                    <RequireAuth>
                      <ChatPage />
                    </RequireAuth>
                  }
                />
              </Route>
              <Route
                path={keys.ROUTE_NAMES.AGENT}
                element={
                  <RequireAgentAccess>
                    <LayoutWithAgentSidebar />
                  </RequireAgentAccess>
                }
              >
                <Route
                  path={keys.ROUTE_NAMES.AGENT}
                  element={
                    <RequireAuth>
                      <LoadableAgentPage />
                    </RequireAuth>
                  }
                />
              </Route>
              <Route path={keys.ROUTE_NAMES.IMPROVING_SALES} element={<LayoutWithMainSidebar />}>
                <Route
                  path={keys.ROUTE_NAMES.IMPROVING_SALES}
                  element={
                    <RequireAuth>
                      <LoadableImprovingSales />
                    </RequireAuth>
                  }
                />
              </Route>
              <Route path={keys.ROUTE_NAMES.CAMPAIGN_DETAIL()} element={<LayoutWithMainSidebar />}>
                <Route
                  index
                  element={
                    <RequireAuth>
                      <LoadableCampaignDetail />
                    </RequireAuth>
                  }
                />
              </Route>

              <Route
                path="/admin"
                element={
                  <RequireAuth>
                    <AdminConsole />
                  </RequireAuth>
                }
              >
                <Route index element={<Navigate to="users" replace />} />
                <Route path="users" element={<UsersBoard />} />
                <Route path="tags" element={<TagsBoard />} />
                <Route
                  path="jobs"
                  element={
                    <RequireJobsAccess>
                      <JobsBoard />
                    </RequireJobsAccess>
                  }
                />
                <Route
                  path="competitions"
                  element={
                    <RequireCompetitionsAccess>
                      <CompetitionsBoard />
                    </RequireCompetitionsAccess>
                  }
                />
                <Route
                  path="prompts"
                  element={
                    <RequirePromptsAccess>
                      <PromptsBoard />
                    </RequirePromptsAccess>
                  }
                />
              </Route>
            </Routes>
            {isLoggedIn && hasValidConfig && <SetupDrawer />}
          </SetupGuard>
        </SetupProvider>
      </>

      <Inactions
        firstStageTime={+firstStage}
        secondStageTime={+secondStage}
        intervalTime={+secondStage - +firstStage}
      />
    </ThemeProvider>
  )
}

export default App
